import { Preferences } from '@capacitor/preferences';
import clsx, { type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import i18n from './i18n';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { jwtDecode } from 'jwt-decode';
import React from 'react';
import { IonRouterLink } from '@ionic/react';
// Tremor Raw cx [v0.0.0]

export function cx(...args: ClassValue[]) {
  return twMerge(clsx(...args));
}

// Tremor Raw focusInput [v0.0.1]

export const focusInput = [
  // base
  'focus:ring-2',
  // ring color
  'focus:ring-blue-200 focus:dark:ring-blue-700/30',
  // border color
  'focus:border-blue-500 focus:dark:border-blue-700',
];

// Tremor Raw focusRing [v0.0.1]

export const focusRing = [
  // base
  'outline outline-offset-2 outline-0 focus-visible:outline-2',
  // outline color
  'outline-blue-500 dark:outline-blue-500',
];

// Tremor Raw hasErrorInput [v0.0.1]

export const hasErrorInput = [
  // base
  'ring-2',
  // border color
  'border-red-500 dark:border-red-700',
  // ring color
  'ring-red-200 dark:ring-red-700/30',
];
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}
export function capitalizeFirstLetter(text: string) {
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
}

export function capitalizeCityName(cityName: string): string {
  if (!cityName) {
    return ('[NULL TEXT]');
  }

  // List of words that should remain lowercase
  const lowercaseWords = ['on', 'the', 'and', 'of', 'in', 'at', 'for', 'with', 'et', 'de'];

  // Function to capitalize the first letter of a word
  const capitalize = (word: string) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();

  // Process each part, handling hyphenated words separately
  return cityName
    .split(' ') // Split by spaces
    .map((word) => {
      // Handle hyphenated words like "Winchester-on-the-Severn"
      const hyphenatedWords = word.split('-');
      return hyphenatedWords
        .map(part => lowercaseWords.includes(part.toLowerCase()) ? part.toLowerCase() : capitalize(part))
        .join('-');
    })
    .join(' '); // Rejoin the words with spaces
}

export function languageToCountryFlag(locale: string) {
  if (locale === 'en') {
    return 'US';
  }
  if (locale === 'zh') {
    return 'CN';
  }
  if (locale === 'ja') {
    return 'JP';
  }

  if (locale === 'ko') {
    return 'KR';
  }

  if (locale === 'vi') {
    return 'VN';
  }
  return locale.toUpperCase();
}

export const getLocale = () => {
  return i18n.language ||
    Preferences.get({ key: 'locale' }).then(res => res.value) ||
    (typeof window !== 'undefined' && window.localStorage.i18nextLng) ||
    'en';
};

export async function isOnboardingDone(token?: string) {
  if (!token) {
    token = (await FirebaseAuthentication.getIdToken()).token;
  }

  const decodedToken = jwtDecode(token) as { onboardingDone: boolean };

  if (decodedToken) {
    return decodedToken?.onboardingDone;
  }

  return false;

}
export function getAnimatedVideoThumbnailUrl(videoId: string): string {
  return videoId.replace('manifest/video.m3u8', 'thumbnails/thumbnail.gif?time=1s&height=600&duration=4s');
}

export function getVideoThumbnailUrl(videoId: string): string {
  return videoId.replace('manifest/video.m3u8', 'thumbnails/thumbnail.jpg');
}

/**
 * Renders text with hashtags highlighted in green and linked to their topic pages
 * @param text - The text content that may contain hashtags
 * @returns React elements with hashtags rendered as links
 */
export const renderCaptionWithHashtags = (text: string): React.ReactNode[] | null => {
  if (!text) return null;

  // Regex to match hashtags (word boundary followed by # and word characters)
  const hashtagRegex = /(#\w+)/g;

  // Split the text by hashtags and create an array of elements
  const parts = text.split(hashtagRegex);

  return parts.map((part, index) => {
    if (part.match(hashtagRegex)) {
      const hashtag = part.substring(1); // Remove the # symbol
      return React.createElement(
        IonRouterLink,
        {
          key: index,
          routerLink: `/discover/topic/${hashtag}`,
          className: "text-green-600 font-medium",
          onClick: (e: React.MouseEvent) => e.stopPropagation()
        },
        part
      );
    }
    return React.createElement(React.Fragment, { key: index }, part);
  });
};

export async function convertDistance(valueInMeters: number): Promise<string> {
  const { value } = await Preferences.get({ key: 'isMetric' });
  const isMetric = value === 'true';

  if (isMetric) {
    return `${valueInMeters} m`;
  } else {
    const yards = valueInMeters * 1.09361;
    return `${yards.toFixed(1)} yd`;
  }
}

export async function toMeters(valueInYards: number): Promise<number> {
  return valueInYards * 0.9144;
}

export async function toYards(valueInMeters: number): Promise<number> {
  return valueInMeters * 1.09361;
}